// Разделить 3MF по цветам: раскрашенная модель → отдельное тело на каждый цвет.
// Тяжёлое (разбор, разрезка) — в worker.js; здесь интерфейс и просмотр.
import { bedPicker, createStage, THREE, type Ctx, type Model, type Part, type Placed } from '@gen3d/core';
import { DEFAULT_OPTS, type CutOpts } from './shape';
import { isBambu, toBambu3MF, type BObject } from './bambu';
import { SKETCH_DEPTH, type MeshMsg, type PieceMsg, type Req, type Res } from './worker';

const T = {
  uk: {
    sample: 'Відкрити з прикладом', sampleErr: 'Не вдалося завантажити приклад — перевірте зʼєднання.', file: 'Файл', pick: 'Обрати 3MF', drop: 'або перетягніть сюди', big: 'Файли понад 10 МБ відкриваються довше.', again: 'Інший файл', local: 'Файл обробляється у вашому браузері й нікуди не надсилається.',
    plate: 'Стіл', plateN: 'Стіл', objs: 'об.', colors: 'Кольори', sep: 'Окремою деталлю', share: 'поверхні',
    sepHint: 'Вимкнений колір зливається з основним кольором деталі.',
    depth: 'Глибина кольору', depthHint: 'Наскільки глибоко колір заходить у модель від поверхні. Тонкі місця фарбуються наскрізь.',
    mm: 'мм', min: 'мін.', max: 'макс.', run: 'Розділити', running: 'Ділимо', stop: 'Зупинити',
    loading: 'Читаємо файл…', explode: 'Рознести деталі', vol: 'см³', ready: 'Готово — можна завантажувати.', copies: 'У 3MF — усі копії зі столу, у ZIP — по одній копії кожного обʼєкта.',
    stale: 'Налаштування змінились — розділіть ще раз.', oneColor: 'У моделі один колір — ділити нічого.',
    err: { BAD_FILE: 'Це не 3MF або файл пошкоджений.', NO_MODEL: 'У файлі немає моделі.', EMPTY: 'У файлі немає обʼєктів.', NOT_MANIFOLD: 'Сітка моделі має дірки — її не вдалося закрити. Спробуйте «Виправити модель» у слайсері й збережіть 3MF ще раз.', FAILED: 'Не вдалося розділити. Спробуйте меншу глибину або інший стіл.', NO_FILE: 'Спершу оберіть файл.', MEMORY: 'Браузеру не вистачило памʼяті. Відокремлюйте частинами — по кілька ділянок за раз.' },
    tooBig: 'Модель завелика: {n} млн трикутників, можна до {max} млн. Спростіть її в слайсері (Simplify model) і збережіть 3MF ще раз.',
    noFile: 'Оберіть 3MF, розфарбований у Bambu Studio або Orca Slicer.', time: 'Велика модель може ділитися кілька хвилин.',
    sepDone: 'Кольори в цій моделі вже розділені на окремі тіла — їх можна розділити й у слайсері (Split → To parts). Тут можна додати замок і штифт.', pickT: 'Що відокремити', pickHint: 'Натисніть на ділянку моделі — вона стане окремою деталлю. З Ctrl — кілька ділянок.', pickTouch: 'Торкніться ділянки моделі — вона стане окремою деталлю. Ще дотик — прибрати.',
    none: 'Натисніть на ділянку моделі, щоб відокремити її.', baseSel: 'Це основа — обрано все інше, воно відокремиться від неї.', area: 'мм²', doneL: 'Відокремлено', pendL: 'Вибрано', dlWait: 'Щоб завантажити, спочатку відокремте обране.', apart: 'Розділити по кольорах', nestSel: 'Виріжеться з відокремленої деталі — гніздо в моделі не зміниться.', back: 'Повернути в модель', backAll: 'Повернути все в модель', remove: 'Прибрати', clear: 'Зняти вибір', cut: 'Відокремити обране',
    cutT: 'Як різати', patch: 'Накладка', lock: 'Замок',
    thin: 'Деталь «{c}» тонка: в середньому {t} мм — накладка вийде крихкою, надійніше різати «Замком».', toLock: 'Різати «Замком»',
    patchHint: 'Зріз — плівка по краю ділянки, повторює форму. Деталь, найімовірніше, друкуватиметься з підтримками.',
    lockHint: 'Деталь заходить у модель пробкою з рівним дном — ним вона лягає на стіл.',
    plugArea: 'Площа дна пробки', deep: 'Глибина дна', deepHint: 'Від нижньої точки краю ділянки: більше — глибша пробка.',
    connT: 'Зʼєднання', cNone: 'Немає', cTri: 'Трикутник', cCyl: 'Циліндр', cRect: 'Квадрат',
    sideT: 'Штифт', sPart: 'На деталі', sBody: 'На моделі', pinW: 'Ширина штифта', pinWHint: 'Діаметр циліндра або сторона квадрата й трикутника. Якщо штифт не вміщується в дно, він зменшується.', pinH: 'Висота штифта', pinMove: 'Штифт можна перетягнути по розрізу.', pinReset: 'Штифт у центр',
    tol: 'Зазор', tolHint: 'На сторону й по глибині. Орієнтир: 0,15 — щільно, 0,2 — звичайно, 0,3 — вільно. Перевірте пробним друком.', tolShort: '0,15 щільно · 0,2 звичайно · 0,3 вільно',
    previewing: 'Рахуємо попередній перегляд…', previewBad: 'Попередній перегляд не вдався — спробуйте інший спосіб.',
    layer: 'шаром', readyCut: 'Готово. Деталі лежать праворуч від моделі рівним боком донизу.', arrange: 'Якщо деталі зачіпають сусідні копії — у слайсері натисніть «Розставити».',
  },
  ru: {
    sample: 'Открыть с примером', sampleErr: 'Не удалось загрузить пример — проверьте соединение.', file: 'Файл', pick: 'Выбрать 3MF', drop: 'или перетащите сюда', big: 'Файлы больше 10 МБ открываются дольше.', again: 'Другой файл', local: 'Файл обрабатывается в вашем браузере и никуда не отправляется.',
    plate: 'Стол', plateN: 'Стол', objs: 'об.', colors: 'Цвета', sep: 'Отдельной деталью', share: 'поверхности',
    sepHint: 'Выключенный цвет сливается с основным цветом детали.',
    depth: 'Глубина цвета', depthHint: 'Насколько глубоко цвет заходит в модель от поверхности. Тонкие места красятся насквозь.',
    mm: 'мм', min: 'мин.', max: 'макс.', run: 'Разделить', running: 'Делим', stop: 'Остановить',
    loading: 'Читаем файл…', explode: 'Разнести детали', vol: 'см³', ready: 'Готово — можно скачивать.', copies: 'В 3MF — все копии со стола, в ZIP — по одной копии каждого объекта.',
    stale: 'Настройки изменились — разделите ещё раз.', oneColor: 'В модели один цвет — делить нечего.',
    err: { BAD_FILE: 'Это не 3MF или файл повреждён.', NO_MODEL: 'В файле нет модели.', EMPTY: 'В файле нет объектов.', NOT_MANIFOLD: 'Сетка модели с дырами — её не удалось закрыть. Попробуйте «Исправить модель» в слайсере и сохраните 3MF ещё раз.', FAILED: 'Не удалось разделить. Попробуйте меньшую глубину или другой стол.', NO_FILE: 'Сначала выберите файл.', MEMORY: 'Браузеру не хватило памяти. Отделяйте частями — по несколько участков за раз.' },
    tooBig: 'Модель слишком большая: {n} млн треугольников, можно до {max} млн. Упростите её в слайсере (Simplify model) и сохраните 3MF ещё раз.',
    noFile: 'Выберите 3MF, раскрашенный в Bambu Studio или Orca Slicer.', time: 'Большая модель может делиться несколько минут.',
    sepDone: 'Цвета в этой модели уже разделены на отдельные тела — их можно разделить и в слайсере (Split → To parts). Здесь можно добавить замок и штифт.', pickT: 'Что отделить', pickHint: 'Нажмите на участок модели — он станет отдельной деталью. С Ctrl — несколько участков.', pickTouch: 'Коснитесь участка модели — он станет отдельной деталью. Ещё касание — убрать.',
    none: 'Нажмите на участок модели, чтобы отделить его.', baseSel: 'Это основа — выбрано всё остальное, оно отделится от неё.', area: 'мм²', doneL: 'Отделено', pendL: 'Выбрано', dlWait: 'Чтобы скачать, сначала отделите выбранное.', apart: 'Разделить по цветам', nestSel: 'Вырежется из отделённой детали — гнездо в модели не изменится.', back: 'Вернуть в модель', backAll: 'Вернуть всё в модель', remove: 'Убрать', clear: 'Снять выбор', cut: 'Отделить выбранное',
    cutT: 'Как резать', patch: 'Накладка', lock: 'Замок',
    thin: 'Деталь «{c}» тонкая: в среднем {t} мм — накладка получится хрупкой, надёжнее резать «Замком».', toLock: 'Резать «Замком»',
    patchHint: 'Срез — плёнка по краю участка, повторяет форму. Деталь, скорее всего, будет печататься с поддержками.',
    lockHint: 'Деталь входит в модель пробкой с ровным дном — им она ложится на стол.',
    plugArea: 'Площадь дна пробки', deep: 'Глубина дна', deepHint: 'От нижней точки края участка: больше — глубже пробка.',
    connT: 'Соединение', cNone: 'Нет', cTri: 'Треугольник', cCyl: 'Цилиндр', cRect: 'Квадрат',
    sideT: 'Штифт', sPart: 'На детали', sBody: 'На модели', pinW: 'Ширина штифта', pinWHint: 'Диаметр цилиндра или сторона квадрата и треугольника. Если штифт не помещается в дно, он уменьшается.', pinH: 'Высота штифта', pinMove: 'Штифт можно перетащить по срезу.', pinReset: 'Штифт в центр',
    tol: 'Зазор', tolHint: 'На сторону и по глубине. Ориентир: 0,15 — плотно, 0,2 — обычно, 0,3 — свободно. Проверьте пробной печатью.', tolShort: '0,15 плотно · 0,2 обычно · 0,3 свободно',
    previewing: 'Считаем предпросмотр…', previewBad: 'Предпросмотр не получился — попробуйте другой способ.',
    layer: 'слоем', readyCut: 'Готово. Детали лежат справа от модели ровной стороной вниз.', arrange: 'Если детали задевают соседние копии — в слайсере нажмите «Расставить».',
  },
  en: {
    sample: 'Open with an example', sampleErr: 'Could not load the example — check your connection.', file: 'File', pick: 'Choose 3MF', drop: 'or drop it here', big: 'Files over 10 MB take longer to open.', again: 'Another file', local: 'The file is processed in your browser and is not uploaded anywhere.',
    plate: 'Plate', plateN: 'Plate', objs: 'obj.', colors: 'Colors', sep: 'Separate part', share: 'of surface',
    sepHint: 'A color that is off merges into the part’s main color.',
    depth: 'Color depth', depthHint: 'How deep the color goes into the model from the surface. Thin areas are colored through.',
    mm: 'mm', min: 'min', max: 'max', run: 'Split', running: 'Splitting', stop: 'Stop',
    loading: 'Reading the file…', explode: 'Explode parts', vol: 'cm³', ready: 'Done — ready to download.', copies: 'The 3MF keeps every copy on the plate; the ZIP has one copy of each object.',
    stale: 'Settings changed — split again.', oneColor: 'The model has a single color — nothing to split.',
    err: { BAD_FILE: 'This is not a 3MF or the file is damaged.', NO_MODEL: 'The file has no model.', EMPTY: 'The file has no objects.', NOT_MANIFOLD: 'The model mesh has holes that could not be closed. Try “Fix model” in your slicer and save the 3MF again.', FAILED: 'Splitting failed. Try a smaller depth or another plate.', NO_FILE: 'Choose a file first.', MEMORY: 'The browser ran out of memory. Separate in parts — a few areas at a time.' },
    tooBig: 'The model is too large: {n} M triangles, the limit is {max} M. Simplify it in your slicer (Simplify model) and save the 3MF again.',
    noFile: 'Choose a 3MF painted in Bambu Studio or Orca Slicer.', time: 'A large model may take a few minutes.',
    sepDone: 'The colors in this model are already separate bodies — your slicer can split them too (Split → To parts). Here you can add a lock and a pin.', pickT: 'What to separate', pickHint: 'Click an area of the model to make it a separate part. Hold Ctrl to pick several.', pickTouch: 'Tap an area of the model to make it a separate part. Tap again to remove it.',
    none: 'Click an area of the model to separate it.', baseSel: 'That is the base — everything else is selected and will be separated from it.', area: 'mm²', doneL: 'Separated', pendL: 'Selected', dlWait: 'Separate the selection first to download.', apart: 'Split by color', nestSel: 'Will be cut out of the separated part — the socket in the model stays the same.', back: 'Put back into the model', backAll: 'Put everything back', remove: 'Remove', clear: 'Clear', cut: 'Separate picked',
    cutT: 'Cut', patch: 'Patch', lock: 'Interlocking',
    thin: 'The “{c}” part is thin: {t} mm on average — an overlay will be fragile, a Lock is more reliable.', toLock: 'Cut with a Lock',
    patchHint: 'The cut is a film across the edge of the area that follows its shape. The part will most likely need supports.',
    lockHint: 'The part goes into the model with a flat-bottomed plug and lies on the bed on that bottom.',
    plugArea: 'Plug bottom area', deep: 'Bottom depth', deepHint: 'From the lowest point of the area edge: more means a deeper plug.',
    connT: 'Connector', cNone: 'None', cTri: 'Triangle', cCyl: 'Cylinder', cRect: 'Square',
    sideT: 'Pin', sPart: 'On the part', sBody: 'On the model', pinW: 'Pin width', pinWHint: 'Cylinder diameter, or the side of the square or triangle. A pin that does not fit the bottom is made smaller.', pinH: 'Pin height', pinMove: 'Drag the pin along the cut to move it.', pinReset: 'Center the pin',
    tol: 'Clearance', tolHint: 'Per side and in depth. Rough guide: 0.15 tight, 0.2 normal, 0.3 loose. Test-fit a print first.', tolShort: '0.15 tight · 0.2 normal · 0.3 loose',
    previewing: 'Building the preview…', previewBad: 'The preview failed — try another method.',
    layer: 'as a layer', readyCut: 'Done. The parts lie to the right of the model, flat side down.', arrange: 'If parts touch neighbouring copies, press “Arrange” in your slicer.',
  },
};

// Цвета на случай файла без списка филаментов (не Bambu/Orca): номера красок 1–16.
const PALETTE = ['#E8E8E8', '#E53935', '#1E88E5', '#43A047', '#FDD835', '#8E24AA', '#FB8C00', '#00ACC1', '#6D4C41', '#212121', '#D81B60', '#7CB342', '#3949AB', '#F4511E', '#00897B', '#9E9E9E'];
const DEPTH = { min: 0.4, max: 3, step: 0.1, def: 1 };

/** Участок меньше этого (мм²) — крошка покраски, щелчок по основе её не выбирает. */
const CRUMB_AREA = 1;
/** Средняя толщина детали, ниже которой «Накладку» советуем заменить «Замком», мм (порог задан пользователем 21.10.2026; белок глаза утки — 0,35, клюв — 2,4). */
const THIN = 2;
/**
 * Треугольников в одном объекте, больше — не режем: браузеру не хватает памяти WASM.
 * Проверено: брелоки 0,97 млн (46 участков разом) режутся, tron 1,82 млн падает и на 15.
 */
const MAX_TRIS = 1_200_000;
const CSS = `
.sc-drop{display:flex;flex-direction:column;align-items:center;gap:6px;padding:18px 12px;border:1.5px dashed var(--line);border-radius:var(--radius-sm);text-align:center;cursor:pointer}
.sc-drop.over{border-color:var(--accent);background:color-mix(in srgb,var(--accent) 7%,transparent)}
.sc-drop .btn{min-height:44px}
.sc-sample{width:100%;min-height:44px;margin-top:8px}
.sc-sample[hidden]{display:none}
.sc-drop.got{flex-direction:row-reverse;justify-content:space-between;gap:10px;padding:6px 6px 6px 10px;text-align:left}
.sc-drop.got small{font:500 13px/1.3 var(--font-mono);color:var(--text);word-break:break-all}
.sc-drop.got .btn{flex:none}
.sc-drop small{color:var(--muted);font-size:13px}
.sc-drop .sc-big{font-size:12px;opacity:.85}
.sc-drop.got .sc-big{display:none}
.sc-note{font-size:13px;line-height:1.35;margin:0 0 6px;padding:8px 10px;border-radius:var(--radius-sm);border-left:3px solid var(--accent);background:color-mix(in srgb,var(--accent) 8%,transparent)}
.sc-note[hidden]{display:none}
.sc-note.warn{border-left-color:var(--bad,#d33);background:color-mix(in srgb,var(--bad,#d33) 8%,transparent)}
.sc-note .sc-clear{min-height:32px;padding:0}
.sc-name{font:500 14px/1.3 var(--font-mono);word-break:break-all}
.sc-col{display:flex;align-items:center;gap:10px;min-height:44px;cursor:pointer}
.sc-col input{width:20px;height:20px;accent-color:var(--accent);flex:none}
.sc-sw{width:22px;height:22px;border-radius:50%;border:1px solid var(--line);flex:none}
.sc-col span{flex:1;min-width:0}
.sc-col small{color:var(--muted);font:400 12px/1 var(--font-mono);white-space:nowrap}
.btn.big.sc-run{margin-top:10px;width:calc(100% - 36px)}
.sc-bar{height:6px;border-radius:3px;background:var(--surface-2);overflow:hidden;margin-top:10px}
.sc-bar i{display:block;height:100%;width:0;background:var(--accent);transition:width .3s}
.sc-msg{font-size:14px;margin-top:8px}
.sc-msg.bad{color:var(--bad)}
.panel select.sc-plate{width:100%;min-height:44px}
.sc-sel{display:flex;align-items:center;gap:10px;min-height:44px}
.sc-sel span{flex:1;min-width:0}
.sc-sel small{color:var(--muted);font:400 12px/1 var(--font-mono);white-space:nowrap}
.sc-sel button{width:44px;height:44px;flex:none;border:0;background:none;color:var(--muted);font-size:22px;line-height:1;cursor:pointer;border-radius:var(--radius-sm)}
.sc-sel button:hover{color:var(--text);background:var(--surface-2)}
.sc-lst{font-size:12px;color:var(--muted);margin:4px 0 0}
.sc-sel button.back{font-size:20px}
.sc-apart{display:block;min-height:44px;margin:-6px 0 2px 28px;padding:0 8px;border:0;background:none;color:var(--accent-text,var(--accent));font:inherit;font-size:13px;cursor:pointer;border-radius:var(--radius-sm)}
.sc-apart:hover{background:var(--surface-2)}
.sc-none{color:var(--muted);font-size:14px;margin:4px 0}
.sc-tiles{display:grid;grid-template-columns:1fr 1fr;gap:6px;margin:2px 0 4px}
.sc-tiles.four{grid-template-columns:1fr 1fr}
.sc-tile{display:flex;align-items:center;justify-content:flex-start;gap:6px;min-height:44px;padding:4px 6px;border:1.5px solid var(--line);border-radius:var(--radius-sm);background:none;color:var(--text);font:inherit;font-size:13px;line-height:1.15;text-align:left;cursor:pointer}
.sc-tile svg{width:30px;height:20px;flex:none}
.sc-tile[aria-pressed=true]{border-color:var(--accent);background:color-mix(in srgb,var(--accent) 9%,transparent)}
.sc-lbl{font-size:12px;color:var(--muted);margin:6px 0 2px}
.sc-row{display:flex;align-items:center;gap:6px;margin:0}
.sc-row>span:first-child{flex:none;width:84px;font-size:13px;line-height:1.15}
.sc-row input[type=range]{flex:1;min-width:0;accent-color:var(--accent);height:28px}
.sc-sub{font-size:11px;color:var(--muted);margin:0 0 4px}
/* скачивание: 3MF и ZIP в одну строку */
.sc-root .actions{flex-direction:row;flex-wrap:wrap;gap:8px}
.sc-root .actions>.btn.big{flex:1 1 0;width:auto;min-width:0;min-height:48px}
.sc-root .actions>.dl-more{flex:none}
.sc-root .dl-more .btn{min-height:48px;padding:0 14px}
.sc-p .sc-sel,.sc-p .sc-col{gap:8px}
.sc-p .btn.big.sc-run{width:calc(100% - 28px)}
/* мышь — плотнее; на пальцах цели остаются от 44px */
@media (pointer:fine){
.sc-p .sc-col,.sc-p .sc-sel{min-height:32px}
.sc-p .sc-sel button{width:32px;height:32px}
.sc-apart{min-height:30px}
.sc-p .sc-drop.got .btn{min-height:34px;padding:0 14px;font-size:14px}
.sc-p .sc-clear{min-height:32px}
.sc-tile{min-height:36px}
.sc-row input[type=range]{height:24px}
}
.sc-go{position:absolute;left:50%;bottom:14px;transform:translateX(-50%);z-index:3;min-height:44px;padding:0 22px;box-shadow:0 6px 20px rgb(0 0 0/.25);white-space:nowrap}
.sc-go[hidden]{display:none}
.sc-status{position:absolute;top:14px;left:50%;transform:translateX(-50%);z-index:3;display:flex;align-items:center;gap:8px;max-width:calc(100% - 28px);padding:8px 14px;border-radius:999px;background:var(--surface);border:1px solid var(--line);box-shadow:0 6px 20px rgb(0 0 0/.18);font-size:13px;white-space:nowrap;pointer-events:none}
.sc-status[hidden]{display:none}
.sc-status i{flex:none;width:14px;height:14px;border-radius:50%;border:2px solid var(--line);border-top-color:var(--accent);animation:sc-spin .8s linear infinite}
.sc-status span{overflow:hidden;text-overflow:ellipsis}
@keyframes sc-spin{to{transform:rotate(360deg)}}
@media (prefers-reduced-motion:reduce){.sc-status i{animation:none}}
@media (max-width:860px){.sc-go{bottom:50px}}
.sc-clear{min-height:44px;padding:0 4px;border:0;background:none;color:var(--accent-text);font:inherit;font-size:14px;cursor:pointer;text-decoration:underline}
`;

// Схемы способов разреза: штриховка — модель, контур — отделяемая деталь
const H = '<defs><pattern id="sc-h" width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><line x1="0" y1="0" x2="0" y2="4" stroke="currentColor" stroke-width="1.2"/></pattern></defs>';
const svg = (body: string) => `<svg viewBox="0 0 60 40" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" aria-hidden="true">${H}${body}</svg>`;
const ICON = {
  patch: svg('<path d="M4 36H56V24Q40 12 24 22Q14 28 4 26Z" fill="url(#sc-h)"/><path d="M4 26Q14 28 24 22Q40 12 56 24V6H4Z"/>'),
  lock: svg('<path d="M4 36H56V20H44L38 31H22L16 20H4Z" fill="url(#sc-h)"/><path d="M4 20H16L22 31H38L44 20H56V6H4Z"/>'),
  none: svg('<rect x="4" y="6" width="24" height="28" fill="url(#sc-h)"/><rect x="28" y="6" width="28" height="28"/>'),
  tri: svg('<rect x="4" y="6" width="24" height="28" fill="url(#sc-h)"/><rect x="28" y="6" width="28" height="28"/><path d="M28 13L41 20L28 27Z" fill="url(#sc-h)"/>'),
  cyl: svg('<rect x="4" y="6" width="24" height="28" fill="url(#sc-h)"/><rect x="28" y="6" width="28" height="28"/><path d="M28 14H35A6 6 0 0 1 35 26H28Z" fill="url(#sc-h)"/>'),
  rect: svg('<rect x="4" y="6" width="24" height="28" fill="url(#sc-h)"/><rect x="28" y="6" width="28" height="28"/><path d="M28 14H39V26H28Z" fill="url(#sc-h)"/>'),
  part: svg('<rect x="4" y="6" width="24" height="28" fill="url(#sc-h)"/><rect x="28" y="6" width="28" height="28"/><path d="M28 14H18V26H28" fill="var(--bg,#fff)"/>'),
  body: svg('<rect x="4" y="6" width="24" height="28" fill="url(#sc-h)"/><rect x="28" y="6" width="28" height="28"/><path d="M28 14H38V26H28Z" fill="url(#sc-h)"/>'),
};
/** Пределы настроек разреза: min, max, шаг. */
const LIM = { area: [1, 100, 1], deep: [-5, 5, 0.1], pinW: [2, 15, 0.5], pinH: [1, 15, 0.5], tol: [0, 1, 0.05] } as const;

type Store = { load(): unknown; save(v: unknown): void };
type Info = Extract<Res, { t: 'structure' }>;

export function mount(root: HTMLElement, { lang, store, sample }: Ctx & { store?: Store }) {
  const t = T[lang] ?? T.uk;
  let depth = DEPTH.def;
  const opts: CutOpts = { ...DEFAULT_OPTS };
  try {
    const v = store?.load() as { depth?: unknown; opts?: Partial<Record<keyof CutOpts, unknown>> } | null;
    if (typeof v?.depth === 'number' && isFinite(v.depth)) depth = Math.min(DEPTH.max, Math.max(DEPTH.min, v.depth));
    const o = v?.opts;
    if (o) {
      if (o.method === 'patch' || o.method === 'lock') opts.method = o.method;
      if (o.conn === 'none' || o.conn === 'tri' || o.conn === 'cyl' || o.conn === 'rect') opts.conn = o.conn;
      if (o.side === 'part' || o.side === 'body') opts.side = o.side;
      for (const k of Object.keys(LIM) as (keyof typeof LIM)[]) { const x = o[k]; if (typeof x === 'number' && isFinite(x)) opts[k] = Math.min(LIM[k][1], Math.max(LIM[k][0], x)); }
    }
  } catch { /* типовые */ }
  const save = () => { try { store?.save({ depth, opts }); } catch { /* сховище недоступне */ } };

  document.head.insertAdjacentHTML('beforeend', `<style>${CSS}</style>`);
  const stage = root.querySelector<HTMLElement>('[data-stage]')!;
  const params = root.querySelector<HTMLElement>('[data-params]')!;
  stage.insertAdjacentHTML('afterbegin', '<button type="button" class="btn primary sc-go" data-go hidden></button><div class="sc-status" data-status role="status" aria-live="polite" hidden><i></i><span></span></div>');
  const viewer = createStage(stage, lang).viewer;
  const view = new THREE.Group();
  viewer.world.add(view);

  const tile = (k: 'method' | 'conn' | 'side', v: string, label: string) =>
    `<button type="button" class="sc-tile" data-set="${k}:${v}" aria-pressed="${opts[k] === v}">${ICON[v as keyof typeof ICON]}<span>${label}</span></button>`;
  /** Заголовок раздела; with — у раздела есть подсказки, ⓘ их показывает. */
  // кнопку «i» к пояснениям добавляет страница — одинаково во всех генераторах
  const head = (text: string, _info = false) => `<summary><b>${text}</b></summary>`;
  const rng = (k: keyof typeof LIM, label: string, unit: string, hint = '') => {
    const [min, max, step] = LIM[k];
    const a = `min="${min}" max="${max}" step="${step}" value="${opts[k]}" aria-label="${label}" data-o="${k}"`;
    return `<div class="sc-row"${hint ? ` title="${hint}"` : ''}><span>${label}</span><input type="range" ${a}>` +
      `<span class="num"><input type="number" ${a} inputmode="decimal"><i>${unit}</i></span></div>`;
  };
  params.innerHTML =
    `<details class="group" open>${head(t.file, true)}` +
    `<label class="sc-drop" data-drop><span class="btn primary">${t.pick}</span><small>${t.drop}</small><small class="sc-big" data-big>${t.big}</small><input type="file" accept=".3mf" hidden data-file></label>` +
    (sample ? `<button type="button" class="btn ghost sc-sample" data-sample>${t.sample}</button>` : '') +
    `<p class="sc-name" data-name hidden></p><p class="hint" data-local>${t.local}</p></details>` +
    `<div data-work hidden>` +
    `<details class="group" open data-plate-g hidden>${head(t.plate)}<label class="field flat"><span class="lbl vh">${t.plate}</span><select class="sc-plate" data-plate></select></label></details>` +
    `<details class="group" open>${head(t.pickT, true)}<p class="sc-note" data-sep hidden>${t.sepDone}</p><p class="hint" data-pick-hint>${matchMedia('(pointer: coarse)').matches ? t.pickTouch : t.pickHint}</p>` +
    `<div data-sel></div><p class="sc-note" data-dl-wait hidden>${t.dlWait}</p><button type="button" class="sc-clear" data-clear hidden>${t.clear}</button></details>` +
    `<details class="group" open data-cut-g hidden>${head(t.cutT, true)}` +
    `<div class="sc-tiles">${tile('method', 'patch', t.patch)}${tile('method', 'lock', t.lock)}</div><p class="hint" data-method-hint></p><p class="sc-note warn" data-thin hidden><span></span> <button type="button" class="sc-clear" data-to-lock>${t.toLock}</button></p>` +
    `<div data-lock>${rng('area', t.plugArea, '%')}${rng('deep', t.deep, t.mm, t.deepHint)}</div>` +
    `<p class="sc-lbl">${t.connT}</p><div class="sc-tiles four">${tile('conn', 'none', t.cNone)}${tile('conn', 'tri', t.cTri)}${tile('conn', 'cyl', t.cCyl)}${tile('conn', 'rect', t.cRect)}</div>` +
    `<div data-conn><p class="sc-lbl">${t.sideT}</p><div class="sc-tiles">${tile('side', 'part', t.sPart)}${tile('side', 'body', t.sBody)}</div>` +
    `${rng('pinW', t.pinW, t.mm, t.pinWHint)}${rng('pinH', t.pinH, t.mm)}<p class="sc-sub">${t.pinMove}</p><button type="button" class="sc-clear" data-pin-reset hidden>${t.pinReset}</button>` +
    `${rng('tol', t.tol, t.mm, t.tolHint)}<p class="sc-sub">${t.tolShort}</p></div></details>` +
    `<details class="group" open>${head(t.depth, true)}<p class="hint">${t.depthHint}</p>` +
    `<div class="rng"><span class="rng-row"><input type="range" min="${DEPTH.min}" max="${DEPTH.max}" step="${DEPTH.step}" value="${depth}" aria-label="${t.depth}" data-depth-r>` +
    `<span class="num"><input type="number" min="${DEPTH.min}" max="${DEPTH.max}" step="${DEPTH.step}" value="${depth}" inputmode="decimal" aria-label="${t.depth}" data-depth-n><i>${t.mm}</i></span></span>` +
    `</div>` +
    `<div class="sc-bar" data-bar hidden><i></i></div><p class="sc-msg" data-msg aria-live="polite"></p></details>` +
    `<details class="group" open data-explode-g hidden>${head(t.explode)}<div class="rng"><span class="rng-row">` +
    `<input type="range" min="0" max="1" step="0.01" value="0" aria-label="${t.explode}" data-explode></span></div></details>` +
    `</div>`;
  params.classList.add('sc-p');
  root.classList.add('sc-root');
  const $ = <E extends HTMLElement>(s: string) => params.querySelector<E>(s)!;
  /** Плашка над моделью: что сейчас считается (чтение файла, предпросмотр, отделение). Пустой текст — убрать. */
  const status = (text: string) => { const s = stage.querySelector<HTMLElement>('[data-status]')!; s.hidden = !text; s.querySelector('span')!.textContent = text; };
  const msg = (text: string, bad = false) => { const m = $('[data-msg]'); m.textContent = text; m.className = 'sc-msg' + (bad ? ' bad' : ''); };

  // Кнопки скачивания страницы — только когда есть результат
  const dl = () => root.querySelectorAll<HTMLButtonElement>('[data-download]');
  const setReady = (on: boolean) => dl().forEach((b) => { b.disabled = !on; });
  setReady(false);
  // страница снимает disabled после скачивания — без результата возвращаем
  new MutationObserver(() => { if (!result) dl().forEach((b) => { if (!b.disabled) b.disabled = true; }); })
    .observe(root, { subtree: true, attributes: true, attributeFilter: ['disabled'] });

  // ---------- поток ----------
  let worker: Worker | null = null;
  const workerURL = import.meta.url.replace(/[^/]*$/, 'worker.js');
  const mfURL = import.meta.resolve('manifold-3d/manifold');
  let onRes: (r: Res) => void = () => {};
  const send = (q: Req, tr: Transferable[] = []) => {
    if (!worker) {
      worker = new Worker(workerURL, { type: 'module' });
      worker.onmessage = (e: MessageEvent<Res>) => (e.data.t === 'preview' ? onPreview(e.data) : onRes(e.data));
      worker.onerror = (e) => { console.error(e); onRes({ t: 'error', code: 'FAILED' }); };
    }
    worker.postMessage(q, tr);
  };
  /**
   * Второй поток — только для наброска: пока основной считает точный предпросмотр (секунды),
   * набросок не стоит в очереди и успевает за ползунком. Тот же файл, своя копия.
   */
  let skw: Worker | null = null;
  const skSend = (q: Req, tr: Transferable[] = []) => {
    if (!skw) {
      skw = new Worker(workerURL, { type: 'module' });
      skw.onmessage = (e: MessageEvent<Res>) => { if (e.data.t === 'sketch') onSketch(e.data); };
      skw.onerror = (e) => console.error(e);
    }
    skw.postMessage(q, tr);
  };
  const skEnd = () => { skw?.terminate(); skw = null; skBusy = false; };
  const ask = <K extends Res['t']>(q: Req, want: K, tr: Transferable[] = [], prog?: (k: number) => void) =>
    new Promise<Extract<Res, { t: K }>>((ok, fail) => {
      onRes = (r) => {
        if (r.t === 'progress') prog?.(r.k);
        else if (r.t === 'error') { if (r.detail) console.error('worker:', r.detail); fail(new Error(r.code)); }
        else if (r.t === want) ok(r as Extract<Res, { t: K }>);
      };
      send(q, tr);
    });
  const errText = (e: unknown) => { const c = (e as Error)?.message as keyof typeof t.err; return t.err[c] ?? t.err.FAILED; };

  // ---------- состояние ----------
  let info: Info | null = null;
  let fileName = 'model';
  let plate = 0;
  let meshes: MeshMsg[] = [];
  let result: PieceMsg[] | null = null;
  let center: [number, number, number] = [0, 0, 0];
  /** С какими настройками отделён участок («сетка:участок»): при переотделении деталь режется так же. */
  const cutOpts = new Map<string, CutOpts>();
  /** Выбранные участки: «сетка:участок». */
  const picked = new Set<string>();
  /**
   * Вложенное отделение: участок вырезается из уже отделённой детали (зрачок из глаза), а не из модели.
   * in — главный участок детали; added — участок выбран ради этого (до того он уходил в деталь невыбранным).
   */
  const nest = new Map<string, { in: number; added: boolean }>();
  /** Вложенное для запроса: [сетка, участок, деталь]; only — только эти ключи. */
  const nestList = (only?: Set<string>) => [...nest].filter(([k]) => picked.has(k) && (!only || only.has(k))).map(([k, v]) => [...k.split(':').map(Number), v.in] as [number, number, number]);
  /** Снять выбор участка: вложенный, выбранный и раньше, остаётся в своей детали. */
  const unsel = (k: string) => { const n = nest.get(k); if (n) { nest.delete(k); if (n.added) picked.delete(k); } else picked.delete(k); };
  let cutMode = false; // результат — отделённые участки, а не все цвета
  let busy = false;
  /** Предпросмотр невырезанного выбора: ключ (выбор + настройки) и детали на своих местах. */
  let pv: { key: string; list: Extract<Res, { t: 'preview' }>['list'] } | null = null;
  let pvId = 0, pvTimer = 0, pvKey = '';
  /** Набросок, пока считается точный предпросмотр: объём, который уйдёт в деталь. */
  let sk: { key: string; sel: string; list: Extract<Res, { t: 'sketch' }>['list'] } | null = null;
  /** Набросок в пути и нужен ли ещё один (настройки поменялись, пока он считался). */
  let skId = 0, skBusy = false, skWant = false, skKey = '';
  /** Перетащенные штифты: «сетка:участок» → точка в координатах сетки. Без записи — штифт в самом толстом месте дна. */
  const pinAt = new Map<string, [number, number, number]>();
  /** Где штифты сейчас (из наброска) — метки для перетаскивания. */
  let marks: { key: string; sel: string; list: Extract<Res, { t: 'sketch' }>['pins'] } | null = null;
  const selNow = () => JSON.stringify([pending().sort(), plate]);
  const pinsNow = () => pending().filter((k) => pinAt.has(k)).map((k) => [...k.split(':').map(Number), pinAt.get(k)!] as [number, number, [number, number, number]]);

  const colorOf = (f: number) => (info?.filaments[f - 1]?.color || PALETTE[(f - 1) % PALETTE.length]).slice(0, 7);
  const typeOf = (f: number) => info?.filaments[f - 1]?.type || '';
  const copiesOf = (obj: number) => info!.objects[obj].at;

  // ---------- просмотр ----------
  /**
   * Что лежит на столе. Объект (до деления — исходный, после — остаток) — ключ «o:объект»,
   * отделённая деталь — «p:сетка:участок». lab — участок каждого треугольника (по нему выбирают следующий).
   */
  type Thing = { key: string; obj: number; mesh: number; pos: Float32Array; tri: Uint32Array; paint: Uint8Array; lab: Uint32Array | null; in?: number; xf?: number[] };
  function things(): Thing[] {
    if (!result) return meshes.map((m, k) => ({ key: `o:${m.obj}`, obj: m.obj, mesh: k, pos: m.pos, tri: m.tri, paint: m.fil, lab: m.reg }));
    const one = (p: PieceMsg) => new Uint8Array(p.tri.length / 3).fill(p.fil);
    if (cutMode) return result.map((p) => p.kind === 'piece'
      ? { key: `p:${p.mesh}:${p.reg}`, obj: p.obj, mesh: p.mesh!, pos: p.pos, tri: p.tri, paint: p.paint ?? one(p), lab: p.lab ?? null, in: p.reg, xf: p.xf }
      : { key: `o:${p.obj}`, obj: p.obj, mesh: p.mesh!, pos: p.pos, tri: p.tri, paint: p.paint ?? one(p), lab: p.lab ?? null });
    return result.map((p) => ({ key: `o:${p.obj}`, obj: p.obj, mesh: -1, pos: p.pos, tri: p.tri, paint: one(p), lab: null }));
  }
  /** Сдвиг копий, перетащенных по столу: ключ → [dx, dy] каждой копии. Переживает деление. */
  const shift = new Map<string, [number, number][]>();
  const shiftOf = (key: string, obj: number) => {
    let s = shift.get(key);
    if (!s) shift.set(key, (s = copiesOf(obj).map(() => [0, 0] as [number, number])));
    return s;
  };
  /** Где стоит каждая копия (относительно центра стола в просмотре и в файле). */
  const atOf = (key: string, obj: number) => {
    const s = shiftOf(key, obj);
    return copiesOf(obj).map((a, i) => [a[0] - center[0] + s[i][0], a[1] - center[1] + s[i][1], a[2] - center[2]] as [number, number, number]);
  };

  const mats = new Map<number, THREE.MeshStandardMaterial>();
  const mat = (f: number) => {
    let m = mats.get(f);
    if (!m) mats.set(f, (m = new THREE.MeshStandardMaterial({ color: new THREE.Color(colorOf(f)), roughness: 0.6, metalness: 0, flatShading: true })));
    return m;
  };
  const ghosts = new Map<number, THREE.MeshStandardMaterial>();
  const ghost = (f: number) => {
    let m = ghosts.get(f);
    if (!m) ghosts.set(f, (m = new THREE.MeshStandardMaterial({ color: new THREE.Color(colorOf(f)), roughness: 0.6, flatShading: true, transparent: true, opacity: 0.18, depthWrite: false })));
    return m;
  };
  function clearView() {
    dropHl();
    for (const m of ghosts.values()) m.dispose();
    ghosts.clear();
    for (const m of pieceMats.values()) m.dispose();
    pieceMats.clear();
    view.traverse((o) => { if ((o as THREE.Mesh).isMesh) (o as THREE.Mesh).geometry.dispose(); });
    view.clear();
    for (const m of mats.values()) m.dispose();
    mats.clear();
  }
  /** Сетка одного цвета, повторённая по копиям объекта; матрицы копий ставит place(). */
  function addMesh(g: THREE.Group, obj: number, m: THREE.Material, geo: THREE.BufferGeometry) {
    const im = new THREE.InstancedMesh(geo, m, copiesOf(obj).length);
    im.frustumCulled = false;
    g.add(im);
    return im;
  }
  /** Расставить копии группы (после сдвига — заново, и луч должен видеть новые места). */
  function place(g: THREE.Group) {
    const at = atOf(g.userData.key, g.userData.obj), m4 = new THREE.Matrix4();
    for (const o of g.children as THREE.InstancedMesh[]) {
      if (o.userData.line) { const p = at[0]; if (p) o.position.set(p[0], p[1], p[2]); continue; }
      at.forEach((p, i) => o.setMatrixAt(i, m4.makeTranslation(p[0], p[1], p[2])));
      o.instanceMatrix.needsUpdate = true;
      o.boundingSphere = null; o.boundingBox = null;
    }
  }
  /** Геометрия из части треугольников сетки (буфер вершин общий). */
  const triGeom = (attr: THREE.BufferAttribute, tri: Uint32Array, ids: ArrayLike<number>) => {
    const idx = new Uint32Array(ids.length * 3);
    for (let i = 0; i < ids.length; i++) { const t = ids[i] * 3; idx[i * 3] = tri[t]; idx[i * 3 + 1] = tri[t + 1]; idx[i * 3 + 2] = tri[t + 2]; }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', attr);
    g.setIndex(new THREE.BufferAttribute(idx, 1));
    return g;
  };
  const geom = (pos: Float32Array, tri: Uint32Array | number[]) => {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setIndex(Array.isArray(tri) ? tri : new THREE.BufferAttribute(tri, 1));
    return g;
  };

  /** Центр стола в просмотре: середина объектов по XY, низ по Z — на столе. Считается при загрузке стола. */
  function measure(list: { obj: number; pos: Float32Array }[]) {
    const lo = [Infinity, Infinity, Infinity], hi = [-Infinity, -Infinity, -Infinity];
    for (const m of list) {
      const l = [Infinity, Infinity, Infinity], h = [-Infinity, -Infinity, -Infinity];
      for (let i = 0; i < m.pos.length; i += 3) for (let c = 0; c < 3; c++) { const v = m.pos[i + c]; if (v < l[c]) l[c] = v; if (v > h[c]) h[c] = v; }
      for (const a of copiesOf(m.obj)) for (let c = 0; c < 3; c++) { lo[c] = Math.min(lo[c], l[c] + a[c]); hi[c] = Math.max(hi[c], h[c] + a[c]); }
    }
    center = [(lo[0] + hi[0]) / 2, (lo[1] + hi[1]) / 2, lo[2]];
    return [hi[0] - lo[0], hi[1] - lo[1], hi[2] - lo[2]];
  }
  /** Габарит всего, что сейчас на столе, со сдвигами. */
  function sizeNow() {
    const lo = [Infinity, Infinity, Infinity], hi = [-Infinity, -Infinity, -Infinity];
    for (const th of things()) {
      const l = [Infinity, Infinity, Infinity], h = [-Infinity, -Infinity, -Infinity];
      for (let i = 0; i < th.pos.length; i += 3) for (let c = 0; c < 3; c++) { const v = th.pos[i + c]; if (v < l[c]) l[c] = v; if (v > h[c]) h[c] = v; }
      for (const a of atOf(th.key, th.obj)) for (let c = 0; c < 3; c++) { lo[c] = Math.min(lo[c], l[c] + a[c]); hi[c] = Math.max(hi[c], h[c] + a[c]); }
    }
    return [hi[0] - lo[0], hi[1] - lo[1], hi[2] - lo[2]];
  }
  function showSize(s = sizeNow()) {
    const d = stage.querySelector<HTMLElement>('[data-dims]')!;
    d.hidden = false;
    d.innerHTML = s.map((v) => `<b>${Math.round(v * 10) / 10}</b>`).join(' × ') + ` ${t.mm}`;
  }

  /** Всё, что на столе: группа на вещь, внутри — по сетке на цвет (один буфер вершин, свои треугольники). */
  function draw() {
    clearView();
    const groups = new Map<string, THREE.Group>();
    for (const th of things()) {
      let g = groups.get(th.key);
      if (!g) { groups.set(th.key, (g = new THREE.Group())); g.userData = { key: th.key, obj: th.obj, things: [] }; view.add(g); }
      g.userData.things.push(th);
      const by = new Map<number, number[]>();
      for (let i = 0; i < th.paint.length; i++) { let a = by.get(th.paint[i]); if (!a) by.set(th.paint[i], (a = [])); a.push(i); }
      const attr = new THREE.BufferAttribute(th.pos, 3);
      for (const [f, ids] of by) {
        const im = addMesh(g, th.obj, mat(f), triGeom(attr, th.tri, ids));
        // по номеру грани — номер треугольника вещи (для выбора участка)
        im.userData = { fil: f, mesh: th.mesh, lab: th.lab, tid: Uint32Array.from(ids), in: th.in };
      }
    }
    for (const g of groups.values()) place(g);
    highlight();
    showSize();
  }

  // ---------- выбор участков ----------
  /** Уже отделённые участки. */
  // деталь забирает и соседей, выбранных вместе с ней, — они тоже отделены
  // вложенный участок, пока он ещё в своей детали, — не отделён
  const cutKeys = () => new Set(cutMode ? result!.filter((p) => p.kind === 'piece').flatMap((p) => (p.regs ?? [p.reg!]).map((r) => `${p.mesh}:${r}`).filter((k) => nest.get(k)?.in !== p.reg)) : []);
  /** Выбранные, но ещё не отделённые. */
  const pending = () => { const c = cutKeys(); return [...picked].filter((k) => !c.has(k)); };
  /** Точка модели → точка детали на столе (xf: поворот 3×3 по строкам и сдвиг). */
  const mv = (p: [number, number, number], xf?: number[]): [number, number, number] => (xf
    ? [0, 1, 2].map((c) => xf[c * 3] * p[0] + xf[c * 3 + 1] * p[1] + xf[c * 3 + 2] * p[2] + xf[9 + c]) as [number, number, number] : p);
  /** Обратно: точка детали → точка модели. */
  const unmv = (v: [number, number, number], xf: number[]): [number, number, number] => {
    const q = [v[0] - xf[9], v[1] - xf[10], v[2] - xf[11]];
    return [0, 1, 2].map((j) => xf[j] * q[0] + xf[3 + j] * q[1] + xf[6 + j] * q[2]) as [number, number, number];
  };
  const moved = (pos: Float32Array, xf?: number[]) => {
    if (!xf) return pos;
    const o = new Float32Array(pos.length);
    for (let i = 0; i < pos.length; i += 3) for (let c = 0; c < 3; c++) o[i + c] = xf[c * 3] * pos[i] + xf[c * 3 + 1] * pos[i + 1] + xf[c * 3 + 2] * pos[i + 2] + xf[9 + c];
    return o;
  };
  let hl: THREE.InstancedMesh[] = [];
  let hlMat: THREE.MeshStandardMaterial | null = null;
  let cutFill: THREE.MeshStandardMaterial | null = null, cutLine: THREE.LineBasicMaterial | null = null;
  /** Предпросмотр как на split3mf: всё полупрозрачное, деталь — своим цветом, срез — акцентом, рёбра — контуром. */
  function cutMats() {
    if (!cutFill) {
      const acc = new THREE.Color(getComputedStyle(document.documentElement).getPropertyValue('--accent').trim() || '#8E84F7');
      cutFill = new THREE.MeshStandardMaterial({ color: acc, emissive: acc, emissiveIntensity: 0.35, roughness: 0.6, flatShading: true, transparent: true, opacity: 0.45, depthWrite: false, side: THREE.DoubleSide });
      cutLine = new THREE.LineBasicMaterial({ color: acc, transparent: true, opacity: 0.95 });
    }
    return { fill: cutFill, line: cutLine! };
  }
  const pieceMats = new Map<number, THREE.MeshStandardMaterial>();
  /** Деталь в предпросмотре — своим цветом, но плотнее модели. */
  const pieceMat = (f: number) => {
    let m = pieceMats.get(f);
    if (!m) pieceMats.set(f, (m = new THREE.MeshStandardMaterial({ color: new THREE.Color(colorOf(f)), roughness: 0.6, flatShading: true, transparent: true, opacity: 0.6, depthWrite: false })));
    return m;
  };
  /** Контур: рёбра, где грани сходятся под углом, — как на split3mf. Копия одна (линии не размножаются по копиям). */
  function outline(g: THREE.Group, attr: THREE.BufferAttribute, tri: Uint32Array, ids: ArrayLike<number>) {
    const src = triGeom(attr, tri, ids);
    const l = new THREE.LineSegments(new THREE.EdgesGeometry(src, 25), cutMats().line);
    src.dispose();
    l.userData.line = true;
    g.add(l);
    hl.push(l as unknown as THREE.InstancedMesh);
  }
  let pinMat: THREE.MeshBasicMaterial | null = null;
  /** Ручки штифтов: шарик в точке штифта на дне, виден сквозь модель. */
  let handles: THREE.Mesh[] = [];
  let pinHit0: THREE.MeshBasicMaterial | null = null;
  function handle(g: THREE.Group, m0: Extract<Res, { t: 'sketch' }>['pins'][number], xf?: number[]) {
    // штифт вложенного — на детали: точка и нормаль поворачиваются вместе с ней
    const m = { ...m0, at: mv(m0.at, xf), n: mv(m0.n, xf && [...xf.slice(0, 9), 0, 0, 0]) };
    // видно только тонкое кольцо по краю штифта (шар закрывал вид); ловит курсор невидимый шар покрупнее
    pinMat ??= new THREE.MeshBasicMaterial({ color: 0xffffff, depthTest: false, transparent: true, opacity: 0.9 });
    pinHit0 ??= new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false, colorWrite: false });
    const h = new THREE.Mesh(new THREE.SphereGeometry(Math.max(m.r * 2, 3.5), 12, 8).translate(m.at[0], m.at[1], m.at[2]), pinHit0);
    const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), new THREE.Vector3(...m.n));
    const ringGeo = new THREE.TorusGeometry(m.r + 0.3, Math.max(0.12, m.r * 0.06), 8, 48).applyQuaternion(q).translate(m.at[0], m.at[1], m.at[2]);
    const ring = new THREE.Mesh(ringGeo, pinMat);
    ring.renderOrder = 10;
    h.add(ring);
    h.userData.line = true;
    h.userData.pin = { key: `${m.mesh}:${m.reg}`, at: m.at, n: m.n, xf };
    g.add(h);
    hl.push(h as unknown as THREE.InstancedMesh, ring as unknown as THREE.InstancedMesh);
    handles.push(h);
  }
  /** Подсветка выбранных участков поверх покраски — в группе вещи, чтобы ехала вместе с ней. */
  function dropHl() {
    for (const o of hl) { o.geometry.dispose(); o.removeFromParent(); }
    hl = [];
    handles = [];
  }
  function highlight() {
    dropHl();
    const want = pending();
    const show = !!want.length && pv?.key === keyNow();
    const draft = !show && !!want.length && sk?.sel === selNow();
    // модель полупрозрачная, пока виден предпросмотр или набросок
    view.traverse((o) => { const u = o.userData; if (u.tid && u.lab) (o as THREE.InstancedMesh).material = show || draft ? ghost(u.fil) : mat(u.fil); });
    if (show || draft) {
      const M = cutMats();
      // вложенное лежит на своей детали (её поворот и сдвиг), остальное — на модели
      const keys = new Set((view.children as THREE.Group[]).map((g) => g.userData.key as string));
      const home = (x: { obj: number; mesh: number; in?: number }) => (x.in !== undefined && keys.has(`p:${x.mesh}:${x.in}`) ? `p:${x.mesh}:${x.in}` : `o:${x.obj}`);
      for (const g of view.children as THREE.Group[]) {
        const key = g.userData.key as string;
        const xf = key.startsWith('p:') ? (g.userData.things[0] as Thing).xf : undefined;
        if (draft) for (const p of sk!.list) {
          if (home(p) !== key) continue;
          const attr = new THREE.BufferAttribute(moved(p.pos, xf), 3), all = Array.from({ length: p.tri.length / 3 }, (_, i) => i);
          hl.push(addMesh(g, p.obj, M.fill, triGeom(attr, p.tri, all)));
          outline(g, attr, p.tri, all);
        }
        if (show) for (const p of pv!.list) {
          if (home(p) !== key) continue;
          const attr = new THREE.BufferAttribute(moved(p.pos, xf), 3), all = Array.from({ length: p.tri.length / 3 }, (_, i) => i);
          const by = new Map<number, number[]>(), cut: number[] = [];
          for (let i = 0; i < p.paint.length; i++) {
            if (p.cut[i]) { cut.push(i); continue; }
            let a = by.get(p.paint[i]); if (!a) by.set(p.paint[i], (a = [])); a.push(i);
          }
          for (const [f, ids] of by) hl.push(addMesh(g, p.obj, pieceMat(f), triGeom(attr, p.tri, ids)));
          if (cut.length) hl.push(addMesh(g, p.obj, M.fill, triGeom(attr, p.tri, cut)));
          outline(g, attr, p.tri, all);
        }
        if (marks?.sel === selNow()) for (const m of marks.list) if (home(m) === key) handle(g, m, xf);
        place(g);
      }
      return;
    }
    if (!want.length) return;
    if (!hlMat) {
      const acc = getComputedStyle(document.documentElement).getPropertyValue('--accent').trim() || '#8E84F7';
      hlMat = new THREE.MeshStandardMaterial({ color: new THREE.Color(acc), emissive: new THREE.Color(acc), emissiveIntensity: 0.45, roughness: 0.5, flatShading: true, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
    }
    for (const g of view.children as THREE.Group[]) {
      for (const th of g.userData.things as Thing[]) {
        if (!th.lab) continue;
        const regs = new Set(want.filter((p) => +p.split(':')[0] === th.mesh).map((p) => +p.split(':')[1]));
        if (!regs.size) continue;
        // свои вершины: общий буфер освободился бы у модели вместе с подсветкой
        const P: number[] = [];
        for (let i = 0; i < th.lab.length; i++) if (regs.has(th.lab[i])) for (let j = 0; j < 3; j++) { const v = th.tri[i * 3 + j] * 3; P.push(th.pos[v], th.pos[v + 1], th.pos[v + 2]); }
        if (!P.length) continue;
        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.Float32BufferAttribute(P, 3));
        hl.push(addMesh(g, th.obj, hlMat, geo));
      }
      place(g);
    }
  }
  /** Участки детали другого цвета, кроме крошек: их можно вырезать из неё. */
  const others = (p: PieceMsg) => {
    const m = meshes[p.mesh!], f = m.regFil[p.reg!];
    return (p.regs ?? []).filter((x) => x !== p.reg && m.regFil[x] !== f && m.regArea[x] >= CRUMB_AREA);
  };
  /** Выбрать в детали все другие цвета — вложенными. */
  function nestAll(p: PieceMsg) {
    for (const x of others(p)) { const k = `${p.mesh}:${x}`; if (!nest.has(k)) nest.set(k, { in: p.reg!, added: !picked.has(k) }); picked.add(k); }
  }
  function renderPicked() {
    const box = $('[data-sel]');
    $('[data-clear]').hidden = picked.size < 2;
    $('[data-cut-g]').hidden = picked.size === 0;
    const pieces = cutMode ? result ?? [] : [];
    // отделённое и выбранное — раздельно: × у отделённой детали возвращает её в модель, и в общем списке
    // это путали со «снять выбор» (отделял нос — глаз возвращался)
    const row = (key: string) => {
      const [k, r] = key.split(':').map(Number);
      const m = meshes[k], f = m.regFil[r];
      // вложенный, ещё не вырезанный, — в списке выбранного, а не как его деталь
      const p = cutSet.has(key) ? pieces.find((x) => x.kind === 'piece' && x.mesh === k && (x.reg === r || !!x.regs?.includes(r))) : undefined;
      const vol = p ? p.vol * copiesOf(p.obj).length : 0;
      const note = p ? `${(vol / 1000).toFixed(vol < 1000 ? 2 : 1)} ${t.vol}${p.flat ? '' : ' · ' + t.layer}` : `${Math.round(m.regArea[r] * copiesOf(m.obj).length)} ${t.area}`;
      const b = p ? `<button type="button" class="back" data-unpick="${key}" aria-label="${t.back}" title="${t.back}">↩</button>`
        : `<button type="button" data-unpick="${key}" aria-label="${t.remove}" title="${t.remove}">×</button>`;
      // деталь из нескольких цветов (глаз: белок и зрачок) — можно разделить по цветам
      const apart = p && p.reg === r && others(p).length ? `<button type="button" class="sc-apart" data-apart="${key}">${t.apart}</button>` : '';
      return `<div class="sc-sel"><i class="sc-sw" style="background:${colorOf(f)}"></i><span>${f}${typeOf(f) ? ' · ' + typeOf(f) : ''}</span><small>${note}</small>${b}</div>${apart}`;
    };
    const cutSet = cutKeys(), done = [...picked].filter((k) => cutSet.has(k)), pend = [...picked].filter((k) => !cutSet.has(k));
    const head = (l: string) => (done.length ? `<p class="sc-lst">${l}</p>` : '');
    box.innerHTML = picked.size
      ? (done.length ? head(t.doneL) + done.map(row).join('') : '') + (pend.length ? head(t.pendL) + pend.map(row).join('') : '')
      : `<p class="sc-none">${t.none}</p>`;
    $('[data-clear]').textContent = done.length ? t.backAll : t.clear;
    box.querySelectorAll<HTMLButtonElement>('[data-unpick]').forEach((b) => b.onclick = () => unpick([b.dataset.unpick!]));
    // разделить по цветам — быстрый путь: выбрать в детали все другие цвета и сразу вырезать их из неё
    box.querySelectorAll<HTMLButtonElement>('[data-apart]').forEach((b) => b.onclick = () => {
      if (busy) return;
      const [k, r] = b.dataset.apart!.split(':').map(Number);
      const p = pieces.find((x) => x.kind === 'piece' && x.mesh === k && x.reg === r);
      if (!p) return;
      for (const x of pending()) unsel(x);
      nestAll(p);
      void run();
    });
    if (busy) return;
    const n = pending().length;
    // выбранное, но не отделённое в файл не попадёт — скачать только после отделения
    setReady(!!result && !n);
    $('[data-dl-wait]').hidden = !(result && n);
    // отделение — кнопкой на поле, пока есть что отделять (деления всех цветов разом больше нет)
    const go = stage.querySelector<HTMLButtonElement>('[data-go]')!;
    go.hidden = !n; go.disabled = false;
    go.textContent = `${t.cut} (${n})`;
    $('[data-pin-reset]').hidden = !pinsNow().length;
  }
  function changed() { highlight(); renderPicked(); schedule(); commit(); }

  // ---------- отмена (Ctrl+Z) и повтор (Ctrl+Shift+Z / Ctrl+Y) ----------
  /** Шаг истории: выбор, что из него отделено, настройки, глубина, штифты. */
  type Snap = { picked: string[]; cut: string[]; opts: CutOpts; depth: number; pins: [string, [number, number, number]][]; per: [string, CutOpts][]; nest: [string, { in: number; added: boolean }][] };
  const snap = (): Snap => ({ picked: [...picked].sort(), cut: [...cutKeys()].filter((k) => picked.has(k)).sort(), opts: { ...opts }, depth, pins: [...pinAt].sort(), per: [...cutOpts].sort(), nest: [...nest].sort() });
  const undo: Snap[] = [], redo: Snap[] = [];
  let restoring = false, commitTimer = 0;
  /** Записать шаг. Ползунок шлёт десятки изменений — они сливаются в один шаг (пауза 400 мс). */
  function commit(now = false) {
    if (restoring) return;
    clearTimeout(commitTimer);
    const go = () => {
      // пока идёт отделение, состояние промежуточное — записываем, когда оно закончится
      if (busy) { commitTimer = window.setTimeout(go, 300); return; }
      commitTimer = 0;
      const s = snap(), top = undo[undo.length - 1];
      if (top && JSON.stringify(top) === JSON.stringify(s)) return;
      undo.push(s); redo.length = 0;
      if (undo.length > 100) undo.shift();
    };
    if (now) go(); else commitTimer = window.setTimeout(go, 400);
  }
  async function restore(S: Snap) {
    restoring = true;
    try {
      Object.assign(opts, S.opts);
      params.querySelectorAll<HTMLInputElement>('[data-o]').forEach((x) => { x.value = String(opts[x.dataset.o as keyof typeof LIM]); });
      renderOpts(); save();
      const depth0 = depth;
      depth = S.depth; dr.value = dn.value = String(depth);
      pinAt.clear(); for (const [k, v] of S.pins) pinAt.set(k, v);
      const want = new Set(S.cut), have = new Set([...cutKeys()].filter((k) => picked.has(k)));
      // другая глубина — отделённое режется заново
      const per0 = JSON.stringify([...cutOpts].sort()), nest0 = JSON.stringify([...nest].sort());
      cutOpts.clear(); for (const [k, v] of S.per ?? []) cutOpts.set(k, v);
      nest.clear(); for (const [k, v] of S.nest ?? []) nest.set(k, v);
      const same = want.size === have.size && [...want].every((k) => have.has(k)) && (depth0 === depth || !want.size) && per0 === JSON.stringify([...cutOpts].sort())
        && nest0 === JSON.stringify([...nest].sort());
      if (!same) {
        if (!want.size) { reset(); msg(''); nest.clear(); for (const [k, v] of S.nest ?? []) nest.set(k, v); }
        else {
          // вложенные, ещё не вырезанные, режутся в своей детали, как до вложенного выбора
          picked.clear(); for (const k of want) picked.add(k);
          for (const [k, v] of nest) if (!want.has(k) && S.picked.includes(k) && want.has(`${k.split(':')[0]}:${v.in}`)) picked.add(k);
          await run(true, want);
        }
      }
      picked.clear(); for (const k of S.picked) picked.add(k);
      highlight(); renderPicked(); schedule();
    } finally { restoring = false; }
  }
  async function travel(back: boolean) {
    if (busy) return;
    if (commitTimer) commit(true);
    if (back) { if (undo.length < 2) return; redo.push(undo.pop()!); await restore(undo[undo.length - 1]); }
    else { const S = redo.pop(); if (!S) return; undo.push(S); await restore(S); }
  }
  window.addEventListener('keydown', (e) => {
    if (!(e.ctrlKey || e.metaKey) || !root.isConnected) return;
    const tag = (e.target as HTMLElement | null)?.tagName;
    if (tag === 'INPUT' && (e.target as HTMLInputElement).type !== 'range' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    const k = e.key.toLowerCase();
    const back = (k === 'z' || e.code === 'KeyZ') && !e.shiftKey;
    const fwd = ((k === 'z' || e.code === 'KeyZ') && e.shiftKey) || k === 'y' || e.code === 'KeyY';
    if (!back && !fwd) return;
    if (!info) return;
    e.preventDefault();
    void travel(back);
  });
  /** Ключ того, что сейчас выбрано и как будет резаться. */
  function keyNow() { return JSON.stringify([pending().sort(), opts, depth, plate, pinsNow(), nestList()]); }
  /**
   * Набросок — сразу на каждое изменение (своим потоком, не чаще, чем он успевает: пока один в пути, следующий ждёт);
   * точный предпросмотр — через паузу после последнего изменения, ответ на старый запрос не показываем.
   */
  function schedule() {
    clearTimeout(pvTimer);
    const key = keyNow();
    if (busy || !pending().length || pv?.key === key) return;
    sketchNow();
    pvTimer = window.setTimeout(() => {
      if (busy) return;
      pvKey = key;
      const id = ++pvId;
      msg(t.previewing); status(t.previewing);
      send({ t: 'preview', id, plate, depth, opts: { ...opts }, pins: pinsNow(), nest: nestList(), sel: [...picked].map((k) => k.split(':').map(Number) as [number, number]) });
    }, 250);
  }
  function sketchNow() {
    if (skBusy) { skWant = true; return; }
    if (sk?.key === keyNow()) return;
    skBusy = true; skWant = false; skKey = keyNow();
    skSend({ t: 'sketch', id: ++skId, plate, opts: { ...opts }, pins: pinsNow(), nest: nestList(), sel: pending().map((k) => k.split(':').map(Number) as [number, number]) });
  }
  function onSketch(r: Extract<Res, { t: 'sketch' }>) {
    if (r.id !== skId) return;
    skBusy = false;
    const sel = JSON.parse(skKey)[0] as string[];
    const selKey = JSON.stringify([sel, JSON.parse(skKey)[3]]);
    sk = { key: skKey, sel: selKey, list: r.list };
    marks = { key: skKey, sel: selKey, list: r.pins };
    // точный предпросмотр уже на месте — набросок не нужен
    if (!(pv && pv.key === keyNow())) highlight();
    if (skWant && !busy && pending().length) sketchNow();
  }
  function onPreview(r: Extract<Res, { t: 'preview' }>) {
    if (r.id !== pvId) return;
    pv = { key: pvKey, list: r.list }; sk = null;
    msg(r.failed ? t.previewBad : note, r.failed); status('');
    highlight();
    thinCheck();
  }
  /**
   * «Накладка» на почти плоском участке (белок глаза) даёт линзу толщиной в доли миллиметра — её не напечатать.
   * Предупреждаем и предлагаем «Замок». Толщина — средняя: объём детали на площадь её участков.
   */
  function thinCheck() {
    let min = Infinity, fil = 0;
    thinKeys = [];
    const low = (v: number, f: number, key?: string) => { if (v < THIN && key) thinKeys.push(key); if (v < min) { min = v; fil = f; } };
    if (opts.method === 'patch') for (const p of pv?.list ?? []) if (p.thin !== undefined) low(p.thin, meshes[p.mesh]?.regFil[p.reg] ?? 0);
    // отделённые — по тому, как резали их самих
    if (cutMode) for (const p of result ?? []) {
      if (p.kind !== 'piece' || p.flat || p.mesh === undefined) continue;
      const key = `${p.mesh}:${p.reg}`;
      if ((cutOpts.get(key)?.method ?? opts.method) !== 'patch') continue;
      const area = (p.regs ?? [p.reg!]).reduce((a, l) => a + (meshes[p.mesh!]?.regArea[l] ?? 0), 0);
      if (area > 0) low(p.vol / area, p.fil, key);
    }
    const el = $('[data-thin]');
    el.hidden = !(min < THIN);
    if (!el.hidden) el.querySelector('span')!.textContent = t.thin.replace('{c}', `${fil}${typeOf(fil) ? ' · ' + typeOf(fil) : ''}`).replace('{t}', min.toLocaleString(lang, { maximumFractionDigits: min < 0.1 ? 2 : 1 }));
  }
  let thinKeys: string[] = [];
  $('[data-to-lock]').onclick = () => {
    // тонкие отделённые детали — «Замком», остальные остаются как были
    const keys = thinKeys;
    for (const k of keys) cutOpts.set(k, { ...cutOpts.get(k)!, method: 'lock' });
    opts.method = 'lock';
    optsChanged();
    if (!(result && cutMode && keys.length)) return;
    const go = () => { if (busy) { window.setTimeout(go, 300); return; } void run(true); };
    go();
  };
  /** Настройки разреза: плитки и числа. */
  function renderOpts() {
    params.querySelectorAll<HTMLButtonElement>('[data-set]').forEach((b) => { const [k, v] = b.dataset.set!.split(':'); b.setAttribute('aria-pressed', String(opts[k as 'method'] === v)); });
    $('[data-lock]').hidden = opts.method !== 'lock';
    $('[data-conn]').hidden = opts.conn === 'none';
    $('[data-method-hint]').textContent = opts.method === 'lock' ? t.lockHint : t.patchHint;
  }
  function optsChanged() { save(); renderOpts(); highlight(); schedule(); commit(); thinCheck(); }
  params.querySelectorAll<HTMLButtonElement>('[data-set]').forEach((b) => b.onclick = () => {
    const [k, v] = b.dataset.set!.split(':');
    (opts as Record<string, unknown>)[k] = v;
    optsChanged();
  });
  params.querySelectorAll<HTMLInputElement>('[data-o]').forEach((inp) => inp.onchange = inp.oninput = () => {
    const k = inp.dataset.o as keyof typeof LIM;
    if (inp.type === 'number' && inp.value === '') return;
    const [min, max, step] = LIM[k];
    const v = Math.min(max, Math.max(min, Math.round((+inp.value || 0) / step) * step));
    opts[k] = +v.toFixed(2);
    params.querySelectorAll<HTMLInputElement>(`[data-o="${k}"]`).forEach((x) => { if (x !== inp) x.value = String(opts[k]); });
    optsChanged();
  });
  renderOpts();
  $('[data-pin-reset]').onclick = () => { for (const k of pending()) pinAt.delete(k); renderPicked(); schedule(); commit(true); };
  /** Вернуть участки на модель. Отделённый — режем заново без него. */
  function unpick(keys: string[]) {
    if (busy) return;
    const c = cutKeys();
    const back = keys.some((k) => c.has(k));
    // вырезанное из детали возвращается в деталь, а не в модель: режем заново
    const inner = keys.some((k) => c.has(k) && nest.has(k));
    for (const k of keys) unsel(k);
    // вложенные в возвращённую деталь больше не вложенные
    for (const k of keys) {
      const [mi, r] = k.split(':').map(Number);
      for (const [x, v] of [...nest]) if (+x.split(':')[0] === mi && v.in === r) unsel(x);
    }
    if (back) {
      for (const k of keys) { shift.delete(`p:${k}`); cutOpts.delete(k); }
      // переотделить, только если отделённое ещё осталось (у детали с вложенными участками в cutKeys их несколько —
      // по счёту ключей выходило «осталось», и пустой выбор запускал разделение всех цветов)
      if ([...picked].some((k) => c.has(k))) { void run(inner); return; }
      reset();
      msg('');
    }
    changed();
  }
  /** Назад к раскрашенной модели (сдвиги объектов остаются). */
  function reset() {
    result = null; cutMode = false; setReady(false);
    cutOpts.clear();
    // деталей нет — вырезать не из чего
    for (const k of [...nest.keys()]) unsel(k);
    $('[data-explode-g]').hidden = true;
    draw();
  }
  /** Что под курсором: попадание в вещь (без подсветки). */
  function hitAt(e: { clientX: number; clientY: number }) {
    return viewer.raycaster(e).intersectObjects(view.children, true).find((h) => h.object.userData.tid) ?? null;
  }
  /** Участок под лучом: «сетка:участок»; заплатки разреза и отделённые детали не выбираются. */
  function regionOf(h: THREE.Intersection | null) {
    const u = h?.object.userData;
    if (!u?.lab || h!.faceIndex == null) return null;
    const r = u.lab[u.tid[h!.faceIndex]] as number;
    return r < meshes[u.mesh].regFil.length ? `${u.mesh}:${r}` : null;
  }
  /** Пояснение к выбору: остаётся после предпросмотра (щелчок по основе). */
  let note = '';
  /** Треугольников в самом большом объекте стола (сверх MAX_TRIS — не режем). */
  let big = 0;
  function click(e: PointerEvent) {
    // после деления всех цветов выбор начинается с раскрашенной модели
    if (result && !cutMode) { reset(); msg(''); }
    if (big > MAX_TRIS) return;
    const hit = hitAt(e), key = regionOf(hit);
    if (!key) return;
    note = '';
    // участок на отделённой детали — вырезать из неё
    const inP = hit!.object.userData.in as number | undefined;
    if (inP !== undefined) { pickInPiece(key, inP, e); return; }
    // основа (самый большой участок) остаётся моделью: щелчок по ней выбирает всё остальное этого объекта,
    // соседние участки уйдут одной деталью (как глаза с клювом). Резать саму основу слоем нельзя — выходит мусор.
    const [mi, ri] = key.split(':').map(Number), m = meshes[mi];
    const base = m.regArea.reduce((b, a, r) => (a > m.regArea[b] ? r : b), 0);
    if (ri === base) {
      // участки цвета основы остаются с ней (у трона чёрное между красными кольцами — не деталь).
      // Крошки (меньше CRUMB_AREA) не выбираем: они и так уходят с соседней деталью, а каждая — лишний проход резки
      // (на утке 40 крошек превращали 6 секунд в зависание)
      const all = m.regFil.map((_, r) => `${mi}:${r}`).filter((k, r) => r !== base && m.regFil[r] !== m.regFil[base] && m.regArea[r] >= CRUMB_AREA);
      // отделять нечего (объект одного цвета) — не обещаем «обрано все інше»
      if (!all.length) { renderColors(); return; }
      const rest = all.filter((k) => !picked.has(k));
      note = t.baseSel;
      if (rest.length) { for (const k of rest) picked.add(k); changed(); }
      msg(note);
      return;
    }
    const add = e.ctrlKey || e.metaKey || e.shiftKey || e.pointerType === 'touch';
    if (add) { if (picked.has(key)) picked.delete(key); else picked.add(key); }
    else {
      // простой щелчок заменяет ещё не отделённый выбор; отделённое остаётся
      const p = pending();
      if (p.length === 1 && p[0] === key) picked.delete(key);
      else { for (const k of p) unsel(k); picked.add(key); }
    }
    changed();
  }

  /** Щелчок по отделённой детали: по её главному участку — все другие цвета, по другому — он один. */
  function pickInPiece(key: string, inP: number, e: PointerEvent) {
    const [mi, ri] = key.split(':').map(Number);
    const p = (result ?? []).find((x) => x.kind === 'piece' && x.mesh === mi && x.reg === inP);
    if (!p) return;
    if (ri === inP) {
      if (!others(p).length) return;
      nestAll(p);
      msg(note = t.baseSel);
      changed();
      return;
    }
    const add = e.ctrlKey || e.metaKey || e.shiftKey || e.pointerType === 'touch';
    const mine = nest.get(key)?.in === inP && pending().includes(key);
    if (add) {
      if (mine) unsel(key);
      else { nest.set(key, { in: inP, added: !picked.has(key) }); picked.add(key); }
    } else {
      const pd = pending();
      if (pd.length === 1 && mine) unsel(key);
      else { for (const k of pd) unsel(k); nest.set(key, { in: inP, added: !picked.has(key) }); picked.add(key); }
    }
    msg(note = pending().length ? t.nestSel : '');
    changed();
  }

  // Мышь: тянуть вещь — двигать по столу, щелчок — выбрать участок, тянуть по пустому месту — вращать.
  // Палец: касание — выбрать, удержание — двигать (иначе модель на телефоне не повернуть).
  type Drag = { id: number; touch: boolean; g: THREE.Group; i: number; plane: THREE.Plane; from: THREE.Vector3; s0: [number, number]; x: number; y: number; t: number; moving: boolean; timer: number; e: PointerEvent };
  let drag: Drag | null = null;
  const onPlane = (e: PointerEvent, plane: THREE.Plane) => {
    const p = new THREE.Vector3();
    return viewer.raycaster(e).ray.intersectPlane(plane, p) ? viewer.world.worldToLocal(p) : null;
  };
  // Ручка штифта: тянется по плоскости дна, после отпускания — новый предпросмотр.
  type PinDrag = { id: number; h: THREE.Mesh; plane: THREE.Plane; key: string; at: [number, number, number]; cur: THREE.Vector3 | null; xf?: number[] };
  let pinDrag: PinDrag | null = null;
  const pinHit = (e: { clientX: number; clientY: number }) => (handles.length ? viewer.raycaster(e).intersectObjects(handles, false)[0]?.object as THREE.Mesh | undefined : undefined);
  stage.addEventListener('pointerdown', (e) => {
    if (pinDrag || drag || busy || e.button !== 0 || !e.isPrimary) return;
    const h = pinHit(e);
    if (!h) return;
    const u = h.userData.pin as { key: string; at: [number, number, number]; n: [number, number, number]; xf?: number[] };
    const g = h.parent!;
    const wp = g.localToWorld(new THREE.Vector3(...u.at).add(h.position));
    const wn = new THREE.Vector3(...u.n).transformDirection(g.matrixWorld);
    pinDrag = { id: e.pointerId, h, plane: new THREE.Plane().setFromNormalAndCoplanarPoint(wn, wp), key: u.key, at: u.at, cur: null, xf: u.xf };
    viewer.controls.enabled = false;
    stage.style.cursor = 'grabbing';
    e.stopPropagation();
  }, true);
  window.addEventListener('pointermove', (e) => {
    const d = pinDrag;
    if (!d) {
      // курсор над ручкой — «взять»
      if (e.pointerType === 'mouse' && !drag && handles.length) stage.style.cursor = pinHit(e) ? 'grab' : '';
      return;
    }
    if (e.pointerId !== d.id) return;
    const p = new THREE.Vector3();
    if (!viewer.raycaster(e).ray.intersectPlane(d.plane, p)) return;
    const g = d.h.parent!, at = atOf(g.userData.key, g.userData.obj)[0];
    const loc = g.worldToLocal(p).sub(new THREE.Vector3(at[0], at[1], at[2]));
    d.cur = loc;
    d.h.position.set(at[0] + loc.x - d.at[0], at[1] + loc.y - d.at[1], at[2] + loc.z - d.at[2]);
  });
  const pinUp = (e: PointerEvent) => {
    const d = pinDrag;
    if (!d || e.pointerId !== d.id) return;
    pinDrag = null;
    viewer.controls.enabled = true;
    stage.style.cursor = '';
    if (!d.cur) return;
    // штифт на детали — обратно в координаты модели
    const c: [number, number, number] = d.xf ? unmv([d.cur.x, d.cur.y, d.cur.z], d.xf) : [d.cur.x, d.cur.y, d.cur.z];
    pinAt.set(d.key, [+c[0].toFixed(2), +c[1].toFixed(2), +c[2].toFixed(2)]);
    renderPicked();
    schedule();
    commit(true);
  };
  window.addEventListener('pointerup', pinUp);
  window.addEventListener('pointercancel', pinUp);
  stage.addEventListener('pointerdown', (e) => {
    if (drag || busy || e.button !== 0 || !e.isPrimary) return;
    const h = hitAt(e);
    if (!h) return;
    let g = h.object.parent as THREE.Group;
    while (g.parent !== view) g = g.parent as THREE.Group;
    const touch = e.pointerType !== 'mouse';
    const i = h.instanceId ?? 0;
    const s = shiftOf(g.userData.key, g.userData.obj)[i];
    drag = {
      id: e.pointerId, touch, g, i, x: e.clientX, y: e.clientY, t: performance.now(), moving: false, e, s0: [s[0], s[1]],
      // плоскость на высоте точки касания: вещь едет под пальцем, а не убегает
      plane: new THREE.Plane(new THREE.Vector3(0, 1, 0), -h.point.y), from: viewer.world.worldToLocal(h.point.clone()), timer: 0,
    };
    if (!touch) { e.stopPropagation(); return; } // камера не поворачивается
    drag.timer = window.setTimeout(() => {
      if (!drag || drag.moving) return;
      drag.moving = true;
      viewer.controls.enabled = false;
      navigator.vibrate?.(15);
    }, 350);
  }, true);
  window.addEventListener('pointermove', (e) => {
    const d = drag;
    if (!d || e.pointerId !== d.id) return;
    if (!d.moving) {
      if (Math.hypot(e.clientX - d.x, e.clientY - d.y) <= 6) return;
      if (d.touch) { clearTimeout(d.timer); drag = null; return; } // палец повёл сразу — вращение камеры
      d.moving = true;
    }
    const p = onPlane(e, d.plane);
    if (!p) return;
    const s = shiftOf(d.g.userData.key, d.g.userData.obj)[d.i];
    s[0] = d.s0[0] + p.x - d.from.x; s[1] = d.s0[1] + p.y - d.from.y;
    place(d.g);
  });
  const up = (e: PointerEvent) => {
    const d = drag;
    if (!d || e.pointerId !== d.id) return;
    drag = null;
    clearTimeout(d.timer);
    viewer.controls.enabled = true;
    if (d.moving) { showSize(); return; }
    if (e.type === 'pointerup' && performance.now() - d.t < 600 && !busy) click(d.e);
  };
  window.addEventListener('pointerup', up);
  window.addEventListener('pointercancel', up);

  let step = 1;
  let order: number[] = [];
  function showResult() {
    draw();
    if (cutMode) return;
    const s = sizeNow();
    // шаг разноса: не меньше высоты модели, а у плоских — заметная доля стола
    step = Math.max(s[2] * 1.3, Math.max(s[0], s[1]) * 0.12, 1);
    // основа — цвет с наибольшим объёмом, она остаётся на столе; остальные — над ней
    const vol = new Map<number, number>();
    for (const p of result!) vol.set(p.fil, (vol.get(p.fil) ?? 0) + p.vol * copiesOf(p.obj).length);
    order = [...vol.keys()].sort((a, b) => vol.get(b)! - vol.get(a)!);
    explode();
  }
  // Разнос: цвета поднимаются друг над другом, основа остаётся на месте
  function explode() {
    const k = +$<HTMLInputElement>('[data-explode]').value;
    view.traverse((o) => { if (o.userData.fil != null) o.position.z = order.indexOf(o.userData.fil) * step * k; });
  }

  // ---------- цвета ----------
  /** Сколько цветов в модели: одноцветную делить не на что, очень плотную — предупредить о времени. */
  function renderColors() {
    if (big > MAX_TRIS) {
      const mln = (n: number) => (n / 1e6).toLocaleString(lang, { maximumFractionDigits: 1 });
      msg(t.tooBig.replace('{n}', mln(big)).replace('{max}', mln(MAX_TRIS)), true);
      return;
    }
    const fils = new Set(meshes.flatMap((m) => m.regFil));
    msg(fils.size < 2 ? t.oneColor : meshes.reduce((s, m) => s + m.fil.length, 0) > 500_000 ? t.time : '');
  }

  function stale() {
    if (!result) return;
    reset();
    msg(t.stale);
  }
  /**
   * Сменили глубину цвета. Отделённые детали не теряем: режем заново с новой глубиной —
   * через паузу после последнего движения ползунка (и после текущего расчёта, если он идёт).
   */
  let recut = 0;
  function depthChanged() {
    if (!(result && cutMode && picked.size)) { stale(); schedule(); return; }
    clearTimeout(recut);
    const go = () => { if (busy) { recut = window.setTimeout(go, 300); return; } void run(); };
    recut = window.setTimeout(go, 400);
  }

  // ---------- файл ----------
  async function open(file: File) {
    if (busy) return;
    busy = true;
    fileName = file.name.replace(/\.3mf$/i, '').replace(/[^\p{L}\p{N}._-]+/gu, '-').slice(0, 60) || 'model';
    const drop = $('[data-drop]');
    drop.classList.add('got');
    drop.querySelector('small')!.textContent = `${file.name} · ${(file.size / 1048576).toFixed(1)} МБ`;
    drop.querySelector('.btn')!.textContent = t.again;
    $('[data-local]').hidden = true;
    $('[data-work]').hidden = false;
    msg(t.loading); status(t.loading);
    result = null; cutMode = false; setReady(false); $('[data-explode-g]').hidden = true;
    try {
      const buf = await file.arrayBuffer();
      worker?.terminate(); worker = null; // прошлый файл — в прошлом потоке
      skEnd();
      const buf2 = buf.slice(0);
      info = await ask({ t: 'open', buf, mf: mfURL }, 'structure', [buf]);
      skSend({ t: 'open', buf: buf2, mf: mfURL }, [buf2]);
      const sel = $<HTMLSelectElement>('[data-plate]');
      sel.innerHTML = info.plates.map((p) => `<option value="${p.id}">${p.name || `${t.plateN} ${p.id || 1}`} · ${p.objects} ${t.objs}</option>`).join('');
      $('[data-plate-g]').hidden = info.plates.length < 2;
      busy = false;
      await loadPlate(info.plates[0].id);
    } catch (e) {
      busy = false;
      clearView();
      status('');
      msg(errText(e), true);
    }
  }
  async function loadPlate(id: number) {
    busy = true;
    // cutMode без result роняет cutKeys(): после отделения новый файл или стол не открывались
    plate = id; result = null; cutMode = false; setReady(false); $('[data-explode-g]').hidden = true;
    msg(t.loading); status(t.loading);
    try {
      meshes = (await ask({ t: 'plate', plate: id }, 'meshes')).list;
      picked.clear(); cutOpts.clear(); pinAt.clear(); nest.clear();
      pv = null; sk = null; pvId++;
      // слишком большой объект показываем, но не режем — и тела заранее не собираем: на них и кончается память
      big = Math.max(0, ...meshes.map((m) => m.tri.length / 3));
      if (big <= MAX_TRIS) {
        send({ t: 'warm', plate: id, depth });
        skSend({ t: 'plate', plate: id });
        skSend({ t: 'warm', plate: id, depth: SKETCH_DEPTH, sketch: true });
      }
      shift.clear();
      const size = measure(meshes);
      draw();
      viewer.frame(size[0], size[1], size[2]);
      renderPicked();
      // как у split3mf: цвета нигде не соприкасаются — модель уже разделена (отделять всё равно можно: замок, штифт)
      $('[data-sep]').hidden = !(meshes.length && meshes.every((m) => m.mixed < 0.01) && new Set(meshes.flatMap((m) => m.regFil)).size > 1);
      // «один цвет» / «долго» — после всего, иначе их стирало
      renderColors();
      // новая модель — история с чистого листа
      undo.length = redo.length = 0; commit(true);
    } catch (e) { console.error(e); msg(errText(e), true); }
    status('');
    busy = false;
  }

  async function run(again = false, only?: Set<string>) {
    if (busy || !info || !picked.size) return;
    busy = true;
    // предпросмотр больше не нужен: ответ на него проигнорируем
    clearTimeout(pvTimer); pvId++; pv = null; sk = null;
    status(t.running + '…');
    const sel = [...picked].map((k) => k.split(':').map(Number) as [number, number]);
    const btn = stage.querySelector<HTMLButtonElement>('[data-go]')!, bar = $('[data-bar]'), fill = bar.querySelector('i')!;
    btn.disabled = true; btn.textContent = t.running + '…';
    bar.hidden = false; fill.style.width = '0';
    msg('');
    const t0 = performance.now();
    try {
      // новые детали режутся текущими настройками, уже отделённые — своими (как их резали)
      // (вложенный участок до вырезания помнит настройки своей детали — режется текущими)
      const pend = new Set(pending());
      const per = [...picked].map((k) => [...k.split(':').map(Number), (!pend.has(k) && cutOpts.get(k)) || { ...opts }] as [number, number, CutOpts]);
      const q: Req = { t: 'cut', plate, depth, sel, opts: { ...opts }, pins: pinsNow(), again, per, nest: nestList(only) };
      const r = await ask(q, 'result', [], (k) => {
        fill.style.width = `${Math.round(k * 100)}%`;
        btn.textContent = `${t.running}… ${Math.round(k * 100)}%`;
        status(btn.textContent);
      });
      result = r.list;
      cutMode = !!r.cut;
      // запомнить, как отделены новые детали (только после удачного отделения)
      for (const k of cutKeys()) if (picked.has(k) && (pend.has(k) || !cutOpts.has(k))) cutOpts.set(k, { ...opts });
      // детали, которых больше нет, забывают свой сдвиг
      const live = new Set(things().map((x) => x.key));
      for (const k of [...shift.keys()]) if (k.startsWith('p:') && !live.has(k)) shift.delete(k);
      showResult();
      renderPicked();
      thinCheck();
      commit(true);
      $('[data-explode-g]').hidden = cutMode;
      setReady(!pending().length);
      const many = result.some((p) => copiesOf(p.obj).length > 1);
      const sec = `${((performance.now() - t0) / 1000).toFixed(0)} ${lang === 'en' ? 's' : 'с'}`;
      msg(cutMode ? `${t.readyCut} ${sec}${many ? ' ' + t.arrange : ''}` : `${t.ready} ${sec}${many ? ' ' + t.copies : ''}`);
    } catch (e) {
      console.error(e);
      msg(errText(e), true);
      // поток после сбоя не переиспользуем: в WASM могла остаться занятая память
      worker?.terminate(); worker = null;
      if (info) { const buf = await lastFile!.arrayBuffer(); await ask({ t: 'open', buf, mf: mfURL }, 'structure', [buf]).catch(() => {}); }
    }
    bar.hidden = true;
    btn.disabled = false;
    status('');
    busy = false;
    renderPicked();
  }

  // ---------- события ----------
  let lastFile: File | null = null;
  const sampleBtn = root.querySelector<HTMLButtonElement>('[data-sample]');
  const take = (f?: File | null) => { if (f) { lastFile = f; if (sampleBtn) sampleBtn.hidden = true; void open(f); } };
  // файл-пример из админки: скачать и открыть как выбранный файл
  if (sample && sampleBtn) {
    const openSample = async () => {
      sampleBtn.disabled = true;
      try {
        const r = await fetch(sample.url);
        if (!r.ok) throw new Error(String(r.status));
        take(new File([await r.blob()], sample.name));
        (window as { gen3dHit?: (e: string) => void }).gen3dHit?.('sample_open');
      } catch (e) { console.warn('sample', e); msg(t.sampleErr, true); }
      sampleBtn.disabled = false;
    };
    sampleBtn.onclick = () => void openSample();
    if (sample.auto) void openSample();
  }
  const input = $<HTMLInputElement>('[data-file]');
  input.onchange = () => { take(input.files?.[0]); input.value = ''; };
  const drop = $('[data-drop]');
  for (const ev of ['dragenter', 'dragover'] as const) drop.addEventListener(ev, (e) => { e.preventDefault(); drop.classList.add('over'); });
  for (const ev of ['dragleave', 'drop'] as const) drop.addEventListener(ev, () => drop.classList.remove('over'));
  drop.addEventListener('drop', (e) => { e.preventDefault(); take(e.dataTransfer?.files[0]); });
  // перетаскивание на модель — тоже
  stage.addEventListener('dragover', (e) => e.preventDefault());
  stage.addEventListener('drop', (e) => { e.preventDefault(); take(e.dataTransfer?.files[0]); });

  $<HTMLSelectElement>('[data-plate]').onchange = (e) => void loadPlate(+(e.target as HTMLSelectElement).value);
  const [dr, dn] = [$<HTMLInputElement>('[data-depth-r]'), $<HTMLInputElement>('[data-depth-n]')];
  const setDepth = (raw: string) => {
    const v = Math.min(DEPTH.max, Math.max(DEPTH.min, Math.round((+raw || DEPTH.def) * 10) / 10));
    depth = v; dr.value = dn.value = String(v);
    save();
    depthChanged();
    commit();
  };
  dr.oninput = () => setDepth(dr.value);
  dn.onchange = () => setDepth(dn.value);
  stage.querySelector<HTMLButtonElement>('[data-go]')!.onclick = () => void run();
  $('[data-clear]').onclick = () => unpick([...picked]);
  $('[data-explode]').oninput = explode;

  // стол с сеткой, как у других генераторов
  const bed = bedPicker(params, lang, (b) => viewer.setBed(b.x, b.y));
  viewer.setBed(bed.x, bed.y);
  viewer.frame(120, 120, 40);
  save();

  /**
   * Отделённые участки: остаток каждого объекта — с покраской (откроется в слайсере раскрашенным),
   * каждая деталь — отдельный объект, целиком покрашенный своим филаментом.
   */
  function cutModel(): Model {
    const parts: Part[] = [];
    const objects: Placed[] = [];
    const nameOf = (obj: number) => info!.objects[obj].name || fileName;
    const rest = new Map<number, Placed>();
    let n = 0;
    for (const p of result!) {
      const g = geom(p.pos, p.tri);
      if (p.kind === 'rest') {
        parts.push({ name: `${nameOf(p.obj)}-${p.obj + 1}`, role: 'base', color: colorOf(p.fil), geometry: g, paint: p.paint });
        extOf.set(parts[parts.length - 1], p.fil);
        let o = rest.get(p.obj);
        if (!o) { rest.set(p.obj, (o = { name: nameOf(p.obj), parts: [], at: atOf(`o:${p.obj}`, p.obj) })); objects.push(o); }
        o.parts.push(parts.length - 1);
      } else {
        const hex = colorOf(p.fil);
        n++;
        parts.push({ name: `${p.fil}-${hex.slice(1)}-${n}`, role: 'detail', color: hex, geometry: g, paint: p.paint ?? new Uint8Array(p.tri.length / 3).fill(p.fil) });
        extOf.set(parts[parts.length - 1], p.fil);
        objects.push({ name: `${nameOf(p.obj)}-${n}`, parts: [parts.length - 1], at: atOf(`p:${p.mesh}:${p.reg}`, p.obj) });
      }
    }
    return { name: `${fileName}-split`, parts, objects };
  }

  // ---------- файл для скачивания ----------
  /** Пластик каждой детали файла (для 3MF Bambu). */
  const extOf = new WeakMap<Part, number>();
  /**
   * Исходник из Bambu Studio / Orca — 3MF собирается из него же: пластики, принтер и профиль сохраняются,
   * а детали получают свои пластики (обычный 3MF Bambu открывает без настроек и без цветов).
   */
  async function withBambu(m: Model): Promise<Model> {
    const f = lastFile;
    if (!f) return m;
    const src = new Uint8Array(await f.arrayBuffer());
    if (!isBambu(src)) return m;
    const objects: BObject[] = (m.objects ?? []).map((o) => ({
      name: o.name,
      // в просмотре стол сдвинут к центру моделей; в файле — исходные координаты стола
      at: o.at.map((a) => [a[0] + center[0], a[1] + center[1], a[2] + center[2]] as [number, number, number]),
      parts: o.parts.map((i) => {
        const p = m.parts[i], g = p.geometry;
        return { name: p.name, pos: g.getAttribute('position').array as Float32Array, tri: Uint32Array.from(g.index!.array as ArrayLike<number>), paint: p.paint, ext: extOf.get(p) ?? 1 };
      }),
    }));
    return { ...m, file: { '3MF': () => toBambu3MF(src, objects, plate) } };
  }
  function model(): Model {
      if (!result) throw new Error(t.err.NO_FILE);
      if (cutMode) return cutModel();
      // деталь = один цвет одного объекта (куски его частей вместе); копии объекта — ссылками в 3MF
      const by = new Map<string, PieceMsg[]>();
      for (const p of result) { const k = `${p.obj}:${p.fil}`; let a = by.get(k); if (!a) by.set(k, (a = [])); a.push(p); }
      const parts: Part[] = [];
      const objects = new Map<number, Placed>();
      for (const list of [...by.values()].sort((a, b) => a[0].obj - b[0].obj || a[0].fil - b[0].fil)) {
        const { obj, fil } = list[0];
        let nv = 0, nt = 0;
        for (const p of list) { nv += p.pos.length / 3; nt += p.tri.length; }
        const pos = new Float32Array(nv * 3), tri = new Uint32Array(nt);
        let v0 = 0, t0 = 0;
        for (const p of list) {
          pos.set(p.pos, v0 * 3);
          for (let i = 0; i < p.tri.length; i++) tri[t0 + i] = p.tri[i] + v0;
          v0 += p.pos.length / 3; t0 += p.tri.length;
        }
        const hex = colorOf(fil);
        parts.push({ name: `${fil}-${hex.slice(1)}${typeOf(fil) ? '-' + typeOf(fil) : ''}`, role: 'base', color: hex, geometry: geom(pos, tri) });
        extOf.set(parts[parts.length - 1], fil);
        let o = objects.get(obj);
        if (!o) objects.set(obj, (o = { name: info!.objects[obj].name || fileName, parts: [], at: atOf(`o:${obj}`, obj) }));
        o.parts.push(parts.length - 1);
      }
      return { name: `${fileName}-split`, parts, objects: [...objects.values()] };
  }
  return { model: () => withBambu(model()) };
}
